import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from "react";
import { initializeApp } from "firebase/app";
import { getAuth, signInWithCustomToken, onAuthStateChanged, signOut, User } from "firebase/auth";
import { getAnalytics } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";
import { useDispatch, useSelector, useStore } from "react-redux";
import { fetchFirebaseBackendTokenThunk } from "slices/auth/thunk";
import { AppDispatch, RootState } from "store";
import apiConfig from "constants/apiConfig";
import { isFresh } from "utils/helpers";

const FIREBASE_TOKEN_STALE_TIME = apiConfig.CREATE_FIREBASE_TOKEN.staleTime;

// Initialize Firebase (singletons for the whole app)
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID
};
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app, "chats");
const realtimeUpdatesDB = getFirestore(app, "realtime");
export const analytics = getAnalytics(app);

interface FirebaseContextProps {
    firebaseUser: User | null;
    loading: boolean;
    auth: typeof auth;
    db: typeof db;
    realtimeUpdatesDB: typeof realtimeUpdatesDB;
    appUser: {
        name: string,
        profile_photo_url: string,
        uuid: string,
        access_type: string,
    } | null;
    analytics: typeof analytics;
}
const FirebaseContext = createContext<FirebaseContextProps>({
    firebaseUser: null,
    loading: true,
    auth,
    db,
    realtimeUpdatesDB,
    appUser: null,
    analytics
});

export const useFirebase = () => useContext(FirebaseContext);
    
export const FirebaseProvider = ({ children }: { children: ReactNode }) => {
    const dispatch: AppDispatch = useDispatch();
    const store = useStore<RootState>();
    const hubId = useSelector((state: RootState) => state.auth.selectedHubId);
    const firebaseCustomToken = useSelector((state: RootState) => state.auth.firebase_tokens[hubId!]);

    const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const appUser = currentUser ? {
        name: currentUser?.name,
        profile_photo_url: currentUser?.profile_photo_url,
        uuid: currentUser?.uuid,
        access_type: currentUser?.access_type,
    } : null;

    // Refs: avoid duplicate signInWithCustomToken loops and parallel fetchFirebaseBackendTokenThunk calls.
    /** Last token we successfully applied with signInWithCustomToken (distinguishes agency/token changes). */
    const lastSignedTokenRef = useRef<string | null>(null);
    /** Prevents overlapping signInWithCustomToken calls while a promise is in flight. */
    const signInLockRef = useRef(false);
    /** Prevents overlapping signOut + fetchFirebaseBackendTokenThunk (main effect, interval, and error path). */
    const refreshInFlightRef = useRef(false);

    /**
     * Clears Firebase session, then loads a fresh custom token for the current agency into Redux.
     * All refresh paths share this so we do not double-hit the API when the main effect and interval
     * (or agency switch + stale token) fire close together.
     */
    const requestFirebaseTokenRefresh = useCallback((force: boolean = false) => {
        if (refreshInFlightRef.current) return;
        refreshInFlightRef.current = true;
        void signOut(auth)
            .catch(() => undefined)
            .then(() => dispatch(fetchFirebaseBackendTokenThunk(force)))
            .finally(() => {
                refreshInFlightRef.current = false;
            });
    }, [dispatch]);

    // Mirror Firebase Auth user into React state for the context provider.
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setFirebaseUser(user);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    /**
     * When Firebase signs out, we can clear the last-token ref. While sign-in is still completing,
     * signInLockRef stays true so we do not wipe lastSignedTokenRef before onAuthStateChanged fires.
     */
    useEffect(() => {
        if (firebaseUser) {
            signInLockRef.current = false;
        } else if (!signInLockRef.current) {
            lastSignedTokenRef.current = null;
        }
    }, [firebaseUser]);

    /**
     * Ensure Redux has a token for the selected agency, then sign into Firebase with it.
     * - Missing/stale token → requestFirebaseTokenRefresh (signOut + fetch thunk).
     * - Valid token → signInWithCustomToken when needed (no session, or token changed e.g. agency switch).
     */
    useEffect(() => {
        if (!currentUser || !hubId) return;

        const tokenMissingOrStale =
            !firebaseCustomToken || !isFresh(firebaseCustomToken.generatedAt, FIREBASE_TOKEN_STALE_TIME);

        if (tokenMissingOrStale) {
            requestFirebaseTokenRefresh();
            return;
        }

        const token = firebaseCustomToken.token;
        const needsSignIn = !firebaseUser || lastSignedTokenRef.current !== token;

        if (!needsSignIn) {
            signInLockRef.current = false;
            return;
        }

        if (signInLockRef.current) return;

        signInLockRef.current = true;
        signInWithCustomToken(auth, token)
            .then(() => {
                lastSignedTokenRef.current = token;
            })
            .catch((err) => {
                console.error(err);
                signInLockRef.current = false;
                requestFirebaseTokenRefresh(true);
            });
    }, [currentUser, hubId, firebaseCustomToken, firebaseUser, requestFirebaseTokenRefresh]);

    /**
     * React effects only re-run when deps change; time passing alone does not. Poll once per minute
     * so long sessions still detect staleness and refresh before the backend token expires.
     */
    useEffect(() => {
        if (!currentUser || !hubId) return;
        const id = window.setInterval(() => {
            const state = store.getState();
            const aid = state.auth.selectedHubId;
            const tok = aid ? state.auth.firebase_tokens[aid] : undefined;
            if (!tok || isFresh(tok.generatedAt, FIREBASE_TOKEN_STALE_TIME)) return;
            requestFirebaseTokenRefresh(true);
        }, 60 * 1000);
        return () => window.clearInterval(id);
    }, [currentUser, hubId, store, requestFirebaseTokenRefresh]);

    return (
        <FirebaseContext.Provider value={{ firebaseUser, loading, auth, db, realtimeUpdatesDB, appUser, analytics }}>
            {children}
        </FirebaseContext.Provider>
    );
};
